import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, type ReactNode } from 'react';
import { api, ApiError, setCsrfToken } from '../lib/api';

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'comercial';
  mustChangePassword: boolean;
}
interface MeResponse {
  user: SessionUser;
  csrfToken: string;
}

interface AuthValue {
  user: SessionUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<SessionUser>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const me = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        const r = await api.get<MeResponse>('/auth/me');
        setCsrfToken(r.csrfToken);
        return r.user;
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          setCsrfToken(null);
          return null;
        }
        throw e;
      }
    },
    staleTime: 60_000,
    retry: false,
  });

  const signIn = useCallback(
    async (email: string, password: string) => {
      const r = await api.post<MeResponse>('/auth/login', { email, password });
      setCsrfToken(r.csrfToken);
      // Limpa os dados de outra sessão, mas mantém a consulta "me" (os componentes já estão inscritos nela).
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
      qc.setQueryData(['me'], r.user);
      return r.user;
    },
    [qc],
  );

  const signOut = useCallback(async () => {
    await api.post('/auth/logout').catch(() => undefined);
    setCsrfToken(null);
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
    qc.setQueryData(['me'], null);
  }, [qc]);

  const refresh = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: ['me'] });
  }, [qc]);

  return (
    <Ctx.Provider value={{ user: me.data ?? null, loading: me.isLoading, signIn, signOut, refresh }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth fora do AuthProvider');
  return v;
}

export const homeFor = (_u: SessionUser) => '/admin';
